import React, { useEffect } from 'react';

import { useHistory, useParams } from 'react-router-dom';

import { ShieldXIcon } from '@primer/octicons-react';
import { StyledOcticon } from '@primer/react';

import upperFirst from 'lodash/upperFirst';

import BlankSlate from '../../common/BlankSlate/BlankSlate';
import ButtonTabGroup from '../../common/ButtonTabGroup/ButtonTabGroup';

import AdminMetricsTab from '../infrastructure/AdminMetricsTab/AdminMetricsTab';
import ModelAdminTab from '../models/ModelAdminTab/ModelAdminTab';
import OrchestrationAdminTab from '../orchestrations/OrchestrationAdminTab/OrchestrationAdminTab';
import PipelineAdminTab from '../pipelines/PipelineAdminTab/PipelineAdminTab';
import UserAdminTab from '../users/UserAdminTab/UserAdminTab';

import PageLayout from '../../pages/PageLayout';

import useClientContext from '../../contexts/ClientData/ClientData';
import usePageTitle from '../../../utils/hooks/use_page_title';
import useUserContext from '../../contexts/UserData/UserData';
import { workspacesValidate } from '../../../openapi';

const TABS = [
  {
    value: 'Users',
    tooltipText: 'Users',
    id: 'users-tab-btn',
  },
  {
    value: 'Pipelines',
    id: 'pipelines-tab-btn',
  },
  {
    value: 'Models',
    id: 'models-tab-btn',
  },
  {
    value: 'Workloads',
    id: 'workloads-tab-btn',
  },
  {
    value: 'Metrics',
    id: 'metrics-tab-btn',
  }
];

const SecurityGraphic = () => <StyledOcticon className="text-error" icon={ShieldXIcon} size={64} />;

export const AdminPage = () => {
  const history = useHistory();
  const routeParams = useParams();
  const { push: setRoute } = history;

  const selectedTab = routeParams?.adminTab || TABS[0].value;

  usePageTitle('Wallaroo Platform Admin Dashboard');

  const { isAdmin } = useUserContext();
  const { isAdminDashboardEnabled } = useClientContext();

  const onItemSelected = itemVal => {
    setRoute(`/admin/${itemVal.toLowerCase()}`);
  };

  // This is for migrations from 2024.1.
  useEffect(() => {
    (async () => {
      await workspacesValidate({ client: window.fetchClient });
    })();
  }, []);

  if (!isAdmin || !isAdminDashboardEnabled) {
    return (
      <PageLayout>
        <div className="w-full">
          <BlankSlate
            mainText="Something went wrong"
            subText="You do not have the necessary permissions to view this page."
            Graphic={SecurityGraphic}
          />
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className="flex flex-col justify-center w-full">
        <div className="mb-4 flex flex-start w-full">
          <ButtonTabGroup
            options={TABS}
            selectedOption={upperFirst(selectedTab)}
            onItemSelected={onItemSelected}
          />
        </div>
        {selectedTab === TABS[0].value.toLowerCase() && <UserAdminTab />}
        {selectedTab === TABS[1].value.toLowerCase() && <PipelineAdminTab />}
        {selectedTab === TABS[2].value.toLowerCase() && <ModelAdminTab />}
        {selectedTab === TABS[3].value.toLowerCase() && <OrchestrationAdminTab />}
        {selectedTab === TABS[4].value.toLowerCase() && <AdminMetricsTab />}
      </div>
    </PageLayout>
  );
};

export default AdminPage;
